/** Angular */
import { Injectable, Injector } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { throwError, Observable } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { Router } from '@angular/router';
/** Sixense */
import { BydAuthService } from '../../services/byd-auth.service';
/** Enums */
import { BydHttpStatusCodeEnum } from '../enums/byd-http-status-code.enum';
import { BydAuthStorageKeysEnum } from '../../enums/byd-auth-storage-keys.enum';
/** Models */
import { BydUserAccountLogin } from '../../models/byd-user-account-login';

@Injectable({
  providedIn: 'root',
})
export class BydAuthInterceptorService implements HttpInterceptor {
  /**
   * Creates an instance of AuthInterceptorService.
   *
   * @param bydAuthService the authentication service
   * @param injector creates a service injector instance
   * @param router contains the information about the associated route
   */
  constructor(private injector: Injector, private router: Router) {}

  /**
   * Intercepts authentication http requests and handles them.
   *
   * @param request The outgoing request to handle
   * @param next The next interceptor in the chain, or the backend if no interceptors in the chain
   * @returns Observable<HttpEvent<T>>
   */
  intercept<T>(request: HttpRequest<T>, next: HttpHandler): Observable<HttpEvent<T>> {
    return next.handle(request).pipe(
      catchError((error) => {
        if (error instanceof HttpErrorResponse) {
          if (error.status === BydHttpStatusCodeEnum.UNAUTHORIZED) {
            return this.handleUnauthorized<T>(request, next);
          }
          if (error.status === BydHttpStatusCodeEnum.INVALID_TOKEN) {
            this.injector.get(BydAuthService).logout();
            this.router.navigateByUrl('/');
          }
        }
        return throwError(error);
      })
    );
  }

  /**
   * Update token in a clone of the original request with an expired token.
   *
   * @param request The outgoing request to handle
   * @param next The next interceptor in the chain, or the backend if no interceptors in the chain
   * @returns Observable<HttpEvent<T>>
   */
  private handleUnauthorized<T>(request: HttpRequest<T>, next: HttpHandler): Observable<HttpEvent<T>> {
    return this.injector
      .get(BydAuthService)
      .refreshLogin()
      .pipe(
        switchMap((response: BydUserAccountLogin) => {
          return next.handle(
            request.clone({ headers: request.headers.set(BydAuthStorageKeysEnum.AUTHORIZATION_KEY, response.token) })
          );
        }),
        catchError((err) => {
          return throwError(err);
        })
      );
  }
}
